import { rpn } from "./calc";
import { actionPriority } from "./action-priority";
import type { PfmeaAfterField, PfmeaRow } from "./types";

/** Package #2.1 — governed PFMEA after-action rating changes. */

export const PFMEA_FIELD_LABEL: Record<PfmeaAfterField, string> = {
  sevAfter: "Severity (after action)",
  occAfter: "Occurrence (after action)",
  detAfter: "Detection (after action)",
};

export function pfmeaChangePreview(row: PfmeaRow, field: PfmeaAfterField, newValue: number) {
  const oldValue = row[field];
  const before = rpn(row.sevAfter, row.occAfter, row.detAfter);
  const next = {
    sevAfter: row.sevAfter,
    occAfter: row.occAfter,
    detAfter: row.detAfter,
    [field]: newValue,
  };
  const after = rpn(next.sevAfter, next.occAfter, next.detAfter);
  return {
    oldValue,
    newValue,
    rpnBefore: before,
    rpnAfter: after,
    rpnDelta: after - before,
    apBefore: actionPriority(row.sevAfter, row.occAfter, row.detAfter),
    apAfter: actionPriority(next.sevAfter, next.occAfter, next.detAfter),
  };
}

/** AP change a request represents, against the row's other current after-action ratings. */
export function requestApChange(
  row: PfmeaRow | undefined,
  req: { field: PfmeaAfterField; oldValue: number; newValue: number },
) {
  if (!row) return null;
  const base = { sevAfter: row.sevAfter, occAfter: row.occAfter, detAfter: row.detAfter };
  const from = { ...base, [req.field]: req.oldValue };
  const to = { ...base, [req.field]: req.newValue };
  return {
    before: actionPriority(from.sevAfter, from.occAfter, from.detAfter),
    after: actionPriority(to.sevAfter, to.occAfter, to.detAfter),
  };
}

/** Returns an error message, or null when the change may be submitted. */
export function validatePfmeaChange(
  row: PfmeaRow | undefined,
  field: PfmeaAfterField,
  newValue: number,
  rationale: string,
): string | null {
  if (!row) return "PFMEA row not found.";
  if (!Number.isInteger(newValue) || newValue < 1 || newValue > 10) return "Ratings must be 1–10.";
  if (row[field] === newValue) return "The new value is the same as the current value.";
  if (!rationale.trim()) return "A rationale is required for a rating change.";
  if (rationale.trim().length < 10) return "The rationale must be at least 10 characters.";
  return null;
}

export function latestRevision(row: PfmeaRow) {
  const list = row.revisions ?? [];
  return list.length ? list[list.length - 1] : undefined;
}
